import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, getToken, setToken } from '../api/client';

interface User {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  streak: number;
  plan: string;
  isPremium?: boolean;
  premiumUntil?: string | null;
  eloRating?: number;
  arenaMatchesPlayed?: number;
}

interface AuthState {
  user: User | null;
  guest: boolean;
  loading: boolean;
  loginWithToken: (token: string) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  loginGuest: () => Promise<void>;
  logout: () => void;
  updateDisplayName: (displayName: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [guest, setGuest] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setGuest(false);
      setLoading(false);
      return;
    }
    try {
      const me = await api<User | { guest: true }>('/auth/me');
      if ('guest' in me && me.guest) {
        setGuest(true);
        setUser(null);
      } else {
        setUser(me as User);
        setGuest(false);
      }
    } catch {
      setToken(null);
      setUser(null);
      setGuest(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginWithToken = useCallback(async (token: string) => {
    setToken(token);
    setGuest(false);
    await refresh();
  }, [refresh]);

  const requestMagicLink = async (email: string) => {
    await api('/auth/magic-link', { method: 'POST', body: JSON.stringify({ email }) });
  };

  const loginGuest = async () => {
    const res = await api<{ guestToken: string }>('/auth/guest', { method: 'POST' });
    setToken(res.guestToken);
    setGuest(true);
    setUser(null);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setGuest(false);
  };

  const updateDisplayName = useCallback(async (displayName: string) => {
    await api<{ displayName: string }>('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    });
    await refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{ user, guest, loading, loginWithToken, requestMagicLink, loginGuest, logout, updateDisplayName }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside provider');
  return ctx;
}
